// Owed's payment message and reminders as WhatsApp wordings Meta approves (docs/plans/whatsapp-ready/STAGE2-PLAN.md
// item 1). Meta lets a business start a conversation only with wording approved in advance, where the changing parts are
// numbered slots ({{1}}, {{2}}) and the rest is fixed. Each round's wording is its own (More > Your messages), so each
// round gets its own copies: the bank details and sign-off are fixed in them, and only the customer's name, the job, the
// amount and the dates change. The pure half: the server (functions/lib/wa-templates.ts) submits and sends with it.
//
// Made by filling the round's own wording, the same as Owed does (chase-message.ts), with a marker in place of each
// changing part; so sending it with that send's parts gives the very words Owed would show, except that a changing part
// is never empty on WhatsApp (Meta refuses an empty slot): no first name greets "there", no job reads "the windows".

import { accountValues, ACCOUNT_WORDING, chaseValues, paymentValues, type ChaseParts, type ChaseRung } from './chase-message'
import { fillTemplate, wordingTemplate, type BusinessSettings } from './business-settings'
import { kindOf, kindOfAll, withoutReview } from './send-all-words'
import { currencySymbol } from './round-config'

export const CLEANING_OFFER_RULE_VERSION = 3

export type PayKind = 'payment' | 'chase_1' | 'chase_2' | 'chase_3' | 'account_1' | 'account_2' | 'account_3'
// Book again (Stage 3, 0099): the one offer, Marketing in Meta's words, with three tap buttons.
export type TemplateKind = PayKind | 'book_again' | 'enquiry_regular'
export const TEMPLATE_KINDS: PayKind[] = ['payment', 'chase_1', 'chase_2', 'chase_3', 'account_1', 'account_2', 'account_3']
export const TEMPLATE_WORDS: Record<TemplateKind, string> = {
  payment: 'Payment message', chase_1: 'Reminder 1', chase_2: 'Reminder 2', chase_3: 'Reminder 3',
  account_1: 'Reminder 1, several visits', account_2: 'Reminder 2, several visits', account_3: 'Reminder 3, several visits',
  book_again: 'Book again', enquiry_regular: 'Offer regular cleans',
}
// Meta's category each kind must have to be sent: payment messages are Utility, never anything else; Book again is an
// offer, so Marketing (Liam, 28 Oct 2026: the one exception to "never offers").
export const KIND_CATEGORY = (kind: TemplateKind): 'UTILITY' | 'MARKETING' => (kind === 'book_again' || kind === 'enquiry_regular' ? 'MARKETING' : 'UTILITY')
const RUNG: Record<PayKind, ChaseRung> = { payment: 'full', chase_1: 'lean', chase_2: 'full', chase_3: 'soft', account_1: 'lean', account_2: 'full', account_3: 'soft' }

// The parts that change from one customer to the next; everything else is fixed in the wording.
const CHANGING = new Set(['first name', 'for the job', 'jobs', 'what was done', 'on date', 'amount', 'from when'])

export interface TemplateShape {
  kind: TemplateKind
  // As Meta gets it: "Hi {{1}},\n\nYour {{2}} have been cleaned. ..."
  body: string
  // For each slot in order, the tags (and the fixed words between them) it is made of.
  slots: { tags: string[]; between: string[] }[]
  // What the wording was made from: a change to any of it is a new wording for Meta.
  fingerprint: string
  // Why Meta would refuse it, so Roundly never submits it ('' when fine).
  problem: string
  // Book again's fixed button words, in order (none for payment messages).
  buttons?: string[]
}

const MARK = (i: number): string => String.fromCharCode(0xe000 + i)
const MARKS = /[-]/

const money = (pence: number | null | undefined): string => (pence ? `${currencySymbol()}${(pence / 100).toFixed(pence % 100 === 0 ? 0 : 2)}` : '')

function valuesFor(kind: PayKind, p: ChaseParts, s: BusinessSettings): Record<string, string> {
  if (kind === 'payment') return paymentValues(p, s)
  if (kind.startsWith('account_')) return accountValues(p.name, p.amountPence ?? 0, RUNG[kind], s)
  return chaseValues(p, RUNG[kind], s).values
}

// Reminder 2's "The amount outstanding is £40." is fixed words round one changing part: only the amount is a slot.
const slotValue = (tag: string, values: Record<string, string>, p: ChaseParts): string => (tag === 'amount line' ? money(p.amountPence) : values[tag] ?? '')

function wordingOf(kind: PayKind, s: BusinessSettings): string {
  if (kind === 'payment') return wordingTemplate('payment_today', s)
  if (kind.startsWith('account_')) return ACCOUNT_WORDING[RUNG[kind]]
  return wordingTemplate(kind as 'chase_1' | 'chase_2' | 'chase_3', s)
}

// A changing part is never empty on WhatsApp.
function filled(tag: string, value: string): string {
  if (value.trim()) return value
  if (tag === 'first name') return 'there'
  if (tag === 'for the job') return 'for the windows'
  if (tag === 'jobs') return 'windows'
  return value
}

// Sample parts for Meta's examples and the shape itself: a priced visit with a name, so every part has words.
const SAMPLE: ChaseParts = { name: 'Sarah Jones', date: '2026-09-22', amountPence: 4000, job: null, today: '2026-10-06' }

export function templateShape(kind: TemplateKind, s: BusinessSettings): TemplateShape {
  if (kind === 'book_again') return bookAgainShape(s)
  if (kind === 'enquiry_regular') return enquiryRegularShape(s)
  const sample = valuesFor(kind, SAMPLE, s)
  const tags = Object.keys(sample)
  const marked: Record<string, string> = {}
  tags.forEach((t, i) => {
    marked[t] = CHANGING.has(t) ? MARK(i) : t === 'amount line' && sample[t] ? sample[t]!.replace(money(SAMPLE.amountPence), MARK(i)) : sample[t]!
  })
  // The review sentence never goes on WhatsApp: a link makes Meta call it marketing.
  const text = withoutReview(fillTemplate(wordingOf(kind, s), marked), s.reviews_link).trim()
  // Neighbouring changing parts, with only spaces between, are one slot (Meta refuses two slots side by side).
  const slots: { tags: string[]; between: string[] }[] = []
  let body = ''
  let i = 0
  while (i < text.length) {
    const ch = text[i]!
    if (!MARKS.test(ch)) { body += ch; i++; continue }
    const slot = { tags: [tags[ch.charCodeAt(0) - 0xe000]!], between: [] as string[] }
    i++
    for (;;) {
      const m = /^([ \t]*)([-])/.exec(text.slice(i))
      if (!m) break
      slot.between.push(m[1]!)
      slot.tags.push(tags[m[2]!.charCodeAt(0) - 0xe000]!)
      i += m[0].length
    }
    slots.push(slot)
    body += `{{${slots.length}}}`
  }
  const problem = /\{\{|\}\}/.test(text) ? 'It has double curly brackets in it, which Meta reads as its own.'
    : !slots.length ? 'It has nothing that changes from one customer to the next.'
    : /^\s*\{\{\d+\}\}/.test(body) || /\{\{\d+\}\}[\s.,!?]*$/.test(body) ? 'It starts or ends with a part that changes, which Meta refuses.'
      : body.length > 1024 ? 'It is too long for WhatsApp (1024 letters at most).'
        : ''
  const fingerprint = `${kind}|${body}`
  return { kind, body, slots, fingerprint, problem }
}

// The slots' words for one send, in order, and the whole message as the customer reads it.
export function templateParts(shape: TemplateShape, p: ChaseParts, s: BusinessSettings): { params: string[]; words: string } | null {
  if (shape.kind === 'book_again' || shape.kind === 'enquiry_regular') return null
  const values = valuesFor(shape.kind, p, s)
  const params = shape.slots.map((slot) => slot.tags.map((t, j) => (j ? slot.between[j - 1] : '') + filled(t, slotValue(t, values, p))).join('').trim())
  // Never a part with nothing in it, a line break or long runs of spaces (Meta refuses them), or no price.
  if (params.some((x) => !x || /[\n\t]| {5,}/.test(x))) return null
  if (!p.amountPence && shape.slots.some((slot) => slot.tags.includes('amount') || slot.tags.includes('amount line'))) return null
  const words = shape.body.replace(/\{\{(\d+)\}\}/g, (_m, n: string) => params[Number(n) - 1] ?? '')
  return { params, words }
}

export function sampleParts(shape: TemplateShape, s: BusinessSettings): string[] {
  if (shape.kind === 'enquiry_regular') return enquiryRegularParts(shape, { name: SAMPLE.name, job: 'your 12 windows', firstA: '£35', everyA: '£22', firstB: '£36', everyB: '£24', once: '£35', onceFirst: '£40' })?.params ?? []
  if (shape.kind === 'book_again') return bookAgainParts(shape, { name: SAMPLE.name, anchor: '£25', everyA: '£18', everyB: '£20' })?.params ?? []
  return templateParts(shape, SAMPLE, s)?.params ?? []
}

// ---- Book again (Stage 3; WORDING.md "Book again, AGREED", Liam 30 Oct 2026) ---------------------------------------
// Liam's own intro, Hormozi's anchor (the one-off price when they call) and one usual choice. Signed with the round's
// sign-off for messages (as the free way's Book again is). Three buttons: the two cycles and No thanks (WhatsApp shows
// three; a fourth would hide them all behind "See options").
export const BOOK_AGAIN_CHOICES = ['a', 'b', 'once'] as const
export type BookAgainChoice = (typeof BOOK_AGAIN_CHOICES)[number] | 'w4' | 'w8' | 'no'

export function bookAgainSignoff(s: BusinessSettings): string {
  return (s.booking_signoff || s.chase_signoff || s.short_name || s.business_name || '').trim()
}

export function bookAgainShape(s: BusinessSettings): TemplateShape {
  const cycles = offerCycles(s)
  const [a, b] = cycles ?? [0, 0]
  const buttons = offerButtons(cycles)
  const sign = bookAgainSignoff(s)
  const body = cycles ? `Hi {{1}}, thanks for using us. Any problems with the clean, let us know and we'll sort them out.\n\nWant to stay on our round and save?\nOne-off clean when you call us: {{2}}\nEvery ${a} weeks: {{3}}\nEvery ${b} weeks: {{4}}\n\n${sign}` : ''
  const problem = !cycles ? 'Choose the two cycles on your buttons on the WhatsApp card first.' : !sign ? 'Add your name for messages in Your business first (it signs the message).'
    : /\{\{|\}\}/.test(sign) ? 'Your name for messages has double curly brackets in it, which Meta reads as its own.'
      : body.length > 1024 ? 'It is too long for WhatsApp (1024 letters at most).' : ''
  const slots = [['first name'], ['anchor'], ['every A'], ['every B']].map((tags) => ({ tags, between: [] as string[] }))
  return { kind: 'book_again', body, slots, fingerprint: `book_again|${body}|${buttons.join('|')}`, problem, buttons }
}

// A price as the message shows it: "£18", or "£18 (£28 with insides)" for a customer who has insides every visit.
export function bookAgainPrice(outsidePence: number, insidePence: number | null): string {
  const out = money(outsidePence)
  return insidePence && insidePence > 0 ? `${out} (${money(outsidePence + insidePence)} with insides)` : out
}

export function bookAgainParts(shape: TemplateShape, p: { name: string | null | undefined; anchor: string; everyA: string; everyB: string }): { params: string[]; words: string } | null {
  const first = (p.name ?? '').trim().split(/\s+/)[0] || 'there'
  const params = [first, p.anchor, p.everyA, p.everyB].map((x) => x.trim())
  if (params.some((x) => !x || /[\n\t]| {5,}/.test(x) || /\{\{|\}\}/.test(x))) return null
  const words = shape.body.replace(/\{\{(\d+)\}\}/g, (_m, n: string) => params[Number(n) - 1] ?? '')
  return { params, words }
}

// Both offer types use one explicit round pair. Price changes are body values, never new button words.
export type OfferCycles = readonly [number, number]
export function validOfferCycles(a: unknown, b: unknown): a is number {
  return typeof a === 'number' && typeof b === 'number' && Number.isInteger(a) && Number.isInteger(b) && a >= 1 && b <= 52 && a < b
}
export function offerCycles(s: Pick<BusinessSettings, 'wa_offer_cycle_a' | 'wa_offer_cycle_b'>): OfferCycles | null {
  return validOfferCycles(s.wa_offer_cycle_a, s.wa_offer_cycle_b) ? [s.wa_offer_cycle_a!, s.wa_offer_cycle_b!] : null
}
export const offerButtons = (cycles: OfferCycles | null): string[] => cycles ? [`Every ${cycles[0]} weeks`, `Every ${cycles[1]} weeks`, 'Just occasionally'] : []

export function enquiryRegularShape(s: BusinessSettings): TemplateShape {
  const cycles = offerCycles(s), [a, b] = cycles ?? [0, 0], sign = bookAgainSignoff(s)
  const body = cycles ? `Hi {{1}},\nFor {{2}}, how often would you like them cleaned?\n\nEvery ${a} weeks: {{3}} a clean, first clean {{4}}\nEvery ${b} weeks: {{5}} a clean, first clean {{6}}\nJust occasionally: {{7}} a clean, first clean {{8}}\n\nTap one and I'll arrange a day with you. Reply STOP to stop WhatsApps.\n\n${sign}` : ''
  const buttons = offerButtons(cycles)
  const problem = !cycles ? 'Choose the two cycles on your buttons on the WhatsApp card first.' : !sign ? 'Add your name for messages in Your business first.'
    : /\{\{|\}\}/.test(sign) ? 'Your name for messages has double curly brackets in it.' : body.length > 1024 ? 'It is too long for WhatsApp.' : ''
  const slots = ['first name', 'job', 'every A', 'first A', 'every B', 'first B', 'occasionally', 'first occasionally'].map(tag => ({ tags: [tag], between: [] as string[] }))
  return { kind: 'enquiry_regular', body, slots, fingerprint: `enquiry_regular|${body}|${buttons.join('|')}`, buttons, problem }
}
export function enquiryRegularParts(shape: TemplateShape, p: { name: string | null | undefined; job: string; everyA: string; firstA: string; everyB: string; firstB: string; once: string; onceFirst: string }): { params: string[]; words: string } | null {
  const first = (p.name ?? '').trim().split(/\s+/)[0] || 'there'
  const params = [first, p.job, p.everyA, p.firstA, p.everyB, p.firstB, p.once, p.onceFirst].map(x => x.trim())
  if (params.some(x => !x || /[\n\t]| {5,}|\{\{|\}\}/.test(x))) return null
  return { params, words: shape.body.replace(/\{\{(\d+)\}\}/g, (_m, n: string) => params[Number(n) - 1] ?? '') }
}

// Meta's name for one version of one round's wording: lower case, digits and underscores.
export const templateName = (kind: TemplateKind, businessId: number, version: number): string => `roundly_${kind}_${businessId}_v${version}`

// One payment message or reminder for Send all or Owed on WhatsApp: which wording, and its words for this person, built
// the same way on the phone (to show) and on the server (to send), so the owner always reads exactly what goes. One visit
// takes its own message; several take the account one at the rung of the one furthest along (several at "payment" read
// as reminder 2's, as sendAllWords does). A brought-over balance never goes this way (plan revision 2, G).
export interface WaWordsVisit { date: string; toCollectPence: number | null; chaseCount: number; job?: string | null }
export function waKindFor(visits: WaWordsVisit[], today: string): PayKind {
  if (visits.length === 1) return kindOf(visits[0]!, today)
  const k = kindOfAll(visits, today)
  return k === 'chase_1' ? 'account_1' : k === 'chase_3' ? 'account_3' : 'account_2'
}
export function waSendWords(p: { name: string | null | undefined; visits: WaWordsVisit[]; today: string; settings: BusinessSettings }):
  { kind: PayKind; shape: TemplateShape; params: string[]; words: string } | { kind: PayKind; problem: string } {
  const kind = waKindFor(p.visits, p.today)
  const shape = templateShape(kind, p.settings)
  if (shape.problem) return { kind, problem: shape.problem }
  const v = p.visits.length === 1 ? p.visits[0]! : null
  const parts: ChaseParts = v
    ? { name: p.name, date: v.date, amountPence: v.toCollectPence, job: kind === 'payment' ? v.job?.trim() || 'windows' : v.job ?? null, today: p.today }
    : { name: p.name, date: null, amountPence: p.visits.reduce((n, x) => n + (x.toCollectPence ?? 0), 0), today: p.today }
  const got = templateParts(shape, parts, p.settings)
  if (!got) return { kind, problem: 'Something in it is missing (a price), so it can only go by Open WhatsApp.' }
  return { kind, shape, ...got }
}
